import React from "react";
import { Routes, Route } from "react-router-dom";
import AdminLayout from "@admin/layouts/AdminLayout";
import RequirePermission from "@admin/components/RequirePermission";
import RequireTwoFactor from "@admin/components/RequireTwoFactor";
import AdminLogin from "@admin/pages/AdminLogin";
import Admin2FA from "@admin/pages/Admin2FA";
import AdminDashboard from "@admin/pages/AdminDashboard";
import AdminPricing from "@admin/pages/AdminPricing";
import AdminLawyers from "@admin/pages/AdminLawyers";
import AdminLawyerReview from "@admin/pages/AdminLawyerReview";
import AdminConsultations from "@admin/pages/AdminConsultations";
import AdminReviews from "@admin/pages/AdminReviews";
import AdminUsers from "@admin/pages/AdminUsers";
import AdminAnalytics from "@admin/pages/AdminAnalytics";
import AdminPayouts from "@admin/pages/AdminPayouts";
import AdminTeam from "@admin/pages/AdminTeam";
import AdminSecurity from "@admin/pages/AdminSecurity";
import AdminKycReview from "@admin/pages/AdminKycReview";
import AdminApplications from "@admin/pages/AdminApplications";
import AdminIdentities from "@admin/pages/AdminIdentities";
import AdminIdentityChanges from "@admin/pages/AdminIdentityChanges";
import AdminFinancialChanges from "@admin/pages/AdminFinancialChanges";
import AdminServiceability from "@admin/pages/AdminServiceability";
import AdminLevelApprovals from "@admin/pages/AdminLevelApprovals";
import AdminListingApprovals from "@admin/pages/AdminListingApprovals";
import { PERMISSIONS } from "@admin/constants/permissions";

/**
 * Admin App — mounted by App.js at REACT_APP_ADMIN_PATH (default /secure-console).
 *
 * Route layering:
 *   /login → AdminLogin (anyone)
 *   /verify → Admin2FA   (admin role required, 2FA satisfied is the WHOLE point)
 *   /*      → RequirePermission (role+session) > RequireTwoFactor > AdminLayout > inner routes
 *
 * Lives entirely under /app/frontend/src/admin/ so the whole module can be
 * lifted into a separate frontend repo (admin.advoset.in) with a 1-folder move.
 */
export default function AdminApp() {
  return (
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route path="verify" element={
        <RequirePermission><Admin2FA /></RequirePermission>
      } />
      <Route path="*" element={
        <RequirePermission>
          <RequireTwoFactor>
            <AdminLayout>
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="pricing" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_PRICING}><AdminPricing /></RequirePermission>
                } />
                <Route path="lawyers" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_LAWYERS}><AdminLawyers /></RequirePermission>
                } />
                <Route path="lawyers/:lid/review" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_LAWYERS}><AdminLawyerReview /></RequirePermission>
                } />
                <Route path="kyc" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_LAWYERS}><AdminKycReview /></RequirePermission>
                } />
                <Route path="applications" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_LAWYERS}><AdminApplications /></RequirePermission>
                } />
                <Route path="approvals/levels" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_PRICING}><AdminLevelApprovals /></RequirePermission>
                } />
                <Route path="approvals/listing" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_LAWYERS}><AdminListingApprovals /></RequirePermission>
                } />
                <Route path="serviceability" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_LAWYERS}><AdminServiceability /></RequirePermission>
                } />
                <Route path="consultations" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_CONSULTATIONS}><AdminConsultations /></RequirePermission>
                } />
                <Route path="reviews" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_REVIEWS}><AdminReviews /></RequirePermission>
                } />
                <Route path="users" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_USERS}><AdminUsers /></RequirePermission>
                } />
                <Route path="payouts" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_PAYOUTS}><AdminPayouts /></RequirePermission>
                } />
                <Route path="analytics" element={
                  <RequirePermission permission={PERMISSIONS.VIEW_ANALYTICS}><AdminAnalytics /></RequirePermission>
                } />
                <Route path="team" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_ADMINS}><AdminTeam /></RequirePermission>
                } />
                <Route path="identities" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_IDENTITIES}><AdminIdentities /></RequirePermission>
                } />
                <Route path="identity-changes" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_IDENTITIES}><AdminIdentityChanges /></RequirePermission>
                } />
                <Route path="financial-changes" element={
                  <RequirePermission permission={PERMISSIONS.MANAGE_IDENTITIES}><AdminFinancialChanges /></RequirePermission>
                } />
                <Route path="security" element={<AdminSecurity />} />
              </Routes>
            </AdminLayout>
          </RequireTwoFactor>
        </RequirePermission>
      } />
    </Routes>
  );
}
